import { useEffect, useState } from "react";
import { ArrowRight, Mail, MapPin } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { api, LIFESTYLE } from "../lib/data";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => window.scrollTo(0, 0), []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent — we'll be in touch.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-28 md:pt-36" data-testid="contact-page">
      <section className="mx-auto grid max-w-[1400px] gap-14 px-5 pb-24 md:grid-cols-2 md:px-10 md:pb-32">
        <Reveal>
          <p className="font-manrope text-xs uppercase tracking-[0.15em] font-bold text-rust">Say hello</p>
          <h1 className="mt-4 font-fraunces text-5xl font-light leading-[1.02] md:text-6xl">
            Tell us about your dog.
          </h1>
          <p className="mt-6 max-w-md font-manrope text-lg text-ink">
            Questions about sizing, an order, or just want to share a photo from the trail? We read every message.
          </p>

          <div className="mt-10 space-y-4 font-manrope">
            <p className="flex items-center gap-3"><Mail className="h-5 w-5 text-forest" /> hello@kindredmile.ca</p>
            <p className="flex items-center gap-3"><MapPin className="h-5 w-5 text-forest" /> Toronto, Canada</p>
          </div>

          <img src={LIFESTYLE.running} alt="A dog running through a field" className="lifestyle-img mt-10 hidden aspect-[16/10] w-full object-cover md:block" />
        </Reveal>

        <Reveal delay={0.1}>
          {sent ? (
            <div className="flex h-full flex-col items-start justify-center rounded-2xl border border-charcoal/15 bg-sand p-10" data-testid="contact-success">
              <h2 className="font-fraunces text-3xl">Thanks — message received.</h2>
              <p className="mt-3 font-manrope text-ink">We'll get back to you shortly. In the meantime, enjoy the walk.</p>
              <button onClick={() => setSent(false)} className="group mt-6 inline-flex items-center gap-2 font-manrope font-semibold hover:text-rust">
                Send another <ArrowRight className="hover-arrow h-4 w-4" />
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
              <div>
                <label className="font-manrope text-sm font-semibold">Name</label>
                <input
                  required
                  data-testid="contact-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-2 w-full rounded-xl border border-charcoal/20 bg-transparent px-4 py-3 font-manrope outline-none focus:border-rust"
                />
              </div>
              <div>
                <label className="font-manrope text-sm font-semibold">Email</label>
                <input
                  required
                  type="email"
                  data-testid="contact-email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-2 w-full rounded-xl border border-charcoal/20 bg-transparent px-4 py-3 font-manrope outline-none focus:border-rust"
                />
              </div>
              <div>
                <label className="font-manrope text-sm font-semibold">Message</label>
                <textarea
                  required
                  rows={6}
                  data-testid="contact-message"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="mt-2 w-full rounded-xl border border-charcoal/20 bg-transparent px-4 py-3 font-manrope outline-none focus:border-rust"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="group flex items-center gap-3 rounded-full bg-rust px-8 py-4 font-manrope font-semibold text-cream transition-colors hover:bg-charcoal disabled:opacity-50"
              >
                {loading ? "Sending…" : "Send message"} <ArrowRight className="hover-arrow h-5 w-5" />
              </button>
            </form>
          )}
        </Reveal>
      </section>
    </div>
  );
}
